/**
 * Functions at Entri - PDF Presentation Configuration
 *
 * Slide-by-slide configuration with:
 * - Page numbers
 * - Pre-scripted narrations for avatar TTS
 */

export const functionsAtEntriPresentation = {
  pdfUrl: 'https://entri-studyplans.s3.ap-south-1.amazonaws.com/media/pdf_notes/2024/11/04/Functions_at_Entri..pdf',

  slides: [
    {
      page: 1,
      title: 'Title Slide',

      narration: `Welcome to Functions at Entri.  Let's learn about how our teams work together.`,
    },
    {
      page: 2,
      title: 'Overview of Functions',
      
      narration: `At a high level,  Entri operates through eleven distinct functions.  Think of these as the gears that keep our engine running.  We're going to break them down one by one so you can understand what each team does and how they contribute to our overall success.   Whether it's Engineering, Product, or User Happiness,   every single function plays a vital role here.`,
    },
    {
      page: 3,
      title: 'Engineering Team',
      
      narration: `First up,  we have the Engineering Team.  You can think of them as the backbone of our product development.  They build and maintain all the technical aspects of our platform,   ensuring reliability, scalability, and performance.   From the front-end interfaces you see to the back-end systems running silently in the background,   this team ensures everything functions seamlessly.   Our product's success relies heavily on their technical expertise and problem-solving skills.`,
    },
    {
      page: 4,
      title: 'Product Team',
      
      narration: `Next is the Product Team.   They are the ones responsible for defining our vision, strategy, and roadmap.   Their goal is to ensure our product meets market demands while aligning with both user needs and our business goals.  They prioritize which features get developed and when,  ensuring that everything we build delivers real value to our users.`,
    },
    {
      page: 5,
      title: 'Design Team',
      
      narration: `Then we have the Design Team.   They focus on creating user-centered, aesthetically pleasing, and functional designs.   They are the driving force behind ensuring every interaction a user has with our app is intuitive and enjoyable.   By balancing functionality with creativity,   they don't just deliver features,   they deliver seamless and delightful experiences.`,
    },
    {
      page: 6,
      title: 'Academics & Content Team',
      
      narration: `As an ed-tech company,   content is king.   Our Academics and Content Team is responsible for ensuring our courses feature the best syllabus and high-quality content.   This talented group of academics, teachers, and creators ensures our courses are well-structured and engaging,   providing an exceptional educational experience for our learners.`,
    },
    {
      page: 7,
      title: 'User Happiness Team',
      
      narration: `User happiness is one of our core organizational values,   and we have a dedicated team to uphold it.   The User Happiness Team ensures every user receives prompt attention and priority.   They work diligently to cultivate a loyal user base,   making sure that every interaction leaves our users feeling satisfied and valued.`,
    },
    {
      page: 8,
      title: 'Media Team',
      
      narration: `Our Media Team plays a crucial role in creating engaging content across all platforms.   They produce everything from in-app video content to promotional marketing videos.   They also manage our presence on YouTube and social media,   ensuring our content resonates with the audience and effectively communicates our mission.`,
    },
    {
      page: 9,
      title: 'Placements Team',
      
      narration: `Since our goal is helping users get job-ready,   our Placements Team is essential.   They bridge the gap between education and employment by providing placement assistance and working with industry partners to create job opportunities.   They also guide learners on resume building and interview prep,   empowering them to achieve their career goals.`,
    },
    {
      page: 10,
      title: 'Growth Team',
      
      narration: `The Growth Team focuses on attracting new users and expanding our market presence.   Using data analysis and marketing campaigns,   they drive user acquisition and retention.   By understanding user behavior,   they identify opportunities to enhance our reach,   ensuring more learners can benefit from our courses.`,
    },
    {
      page: 11,
      title: 'Inside Sales Team',
      
      narration: `Our Inside Sales Team is actually the largest team at Entri.   They are dedicated to driving revenue by reaching out to potential clients and providing exceptional service throughout the sales process.   Their commitment is essential for connecting with more users and helping them achieve their educational aspirations.`,
    },
    {
      page: 12,
      title: 'Founder\'s Office',
      
      narration: `The Founder's Office plays a critical role in strategic planning and executive decision-making.   They support the CEO in driving the company's vision and mission,   ensuring all initiatives align with our long-term goals.   This team facilitates communication between leadership and other departments to maintain focus on key priorities.`,
    },
    {
      page: 13,
      title: 'Human Resources',
      
      narration: `The HR function is vital for managing our most important asset,   you, the employees.   They handle recruitment, onboarding, and training to ensure a positive work environment.   By focusing on employee development and engagement,   HR helps align your individual goals with the company's objectives.`,
    },
    {
      page: 14,
      title: 'Finance & Accounting',
      
      narration: `The Finance and Accounting team is crucial for keeping Entri financially healthy.   They manage budgeting, compliance, payroll, and expenses.   By ensuring accurate financial records,   they help us make informed decisions and identify cost-saving opportunities,   supporting our overall growth and efficiency.`,
    },
    {
      page: 15,
      title: 'Administration & Operations',
      
      narration: `And finally,   our Administration and Operations Team.   They are key to keeping everything running smoothly by managing assets and handling daily administrative tasks.   They streamline processes to allow other teams to focus on their main goals,   helping us succeed as a whole.`,
    },
    {
      page: 16,
      title: 'Core Team Members',
      
      narration: `Before we wrap up,   I want to introduce you to some of the core team members you might see around.   We have our Co-Founder and CEO, Mohammed Hisamuddin,   and Co-Founder and CTO, Rahul Ramesh.   We also have leaders across all functions,   including Hrishikesh Bhaskaran, VP of Engineering,   Ratheesh Sebastian, Head of Finance and Operations,   and many others leading our Growth, Academics, and User Happiness teams.`,
    },
    {
      page: 17,
      title: 'Functional Leadership',
      
      narration: `Moving on,   I'd like to introduce you to the key leaders driving our major functions.   On this slide, you see the individuals heading up critical departments such as Engineering, Finance, Growth, and Academics.   These leaders, including our VPs and Heads of Operations,   work closely with the founders to execute our strategy and ensure that every department,   from Content Operations to User Happiness,   is aligned with our mission.`,
    },
    {
      page: 18,
      title: 'Extended Leadership & Operational Heads',
      
      narration: `Here we have more of our integral team leaders who manage specific verticals and operational areas.   This group includes our Heads of HR, Media, and Product Design,   as well as leaders managing specific regions and categories like Study Abroad and Corporate Relations.   Their specialized expertise ensures that every aspect of our business runs smoothly and that we continue to innovate and expand our reach effectively.`,
    },
  ],
};

export default functionsAtEntriPresentation;
